import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CameraCapability, CameraError } from '../utils';
import './CameraUnavailable.css';

// VISCAT-359: friendly, cause-specific message shown on the QR-scan page when
// the camera cannot be used. Presentational only — driven by `reason` (from
// checkCameraPreflight / classifyCameraError) and `isStandalone`. There is no
// manual code-entry fallback by design; the guidance is aimed at a supervising
// adult so a blocked candidate is never left on a dead-end screen.

const SUPPORTED_BROWSERS = [
    'iPad / iPhone Safari 15.4 or newer',
    'Mac Safari 15.4 or newer',
    'Chrome 108 or newer',
    'Edge 108 or newer',
];

// One entry per reason. `showBrowsers` adds the supported-browser list for the
// cases where switching/updating the browser is the fix.
const COPY = {
    [CameraCapability.BROWSER_UNSUPPORTED]: {
        title: 'Please update this browser',
        body: 'This game needs a newer web browser to scan the QR code. Ask a grown-up to update the browser or use a different device, then try again.',
        showBrowsers: true,
    },
    [CameraCapability.CAMERA_API_ABSENT]: {
        title: "This browser can't use the camera",
        body: 'This browser will not let the scanner use the camera. Ask a grown-up to open the game in a different browser or on another device.',
        showBrowsers: true,
    },
    [CameraCapability.INSECURE_CONTEXT]: {
        title: 'A secure connection is needed',
        body: 'The camera can only start over a secure (https) link. Please ask a grown-up for help.',
    },
    [CameraError.PERMISSION_DENIED]: {
        title: 'Camera access is turned off',
        body: 'The camera is blocked for this game. A grown-up can turn it back on in the device settings, then try again.',
    },
    [CameraError.NO_CAMERA]: {
        title: 'No camera found',
        body: 'We could not find a camera on this device. Please ask a grown-up for help, or use a device that has a camera.',
    },
    [CameraError.CAMERA_IN_USE]: {
        title: 'The camera is busy',
        body: 'Another app is using the camera right now. Please close other camera apps and try again.',
    },
    [CameraError.UNKNOWN]: {
        title: "We can't start the camera",
        body: 'Something stopped the camera from starting. Please ask a grown-up for help.',
    },
};

// Extra recovery steps for a denied permission inside an installed (PWA) app,
// where the toggle lives in the device Settings app rather than the browser.
const IPAD_STEPS = [
    'Open the iPad Settings app',
    'Find this app near the bottom of the list',
    'Turn on the Camera permission',
    'Come back to this page and try again',
];

const CameraUnavailable = ({ reason, isStandalone = false }) => {
    const navigate = useNavigate();
    const copy = COPY[reason] || COPY[CameraError.UNKNOWN];
    const showIpadSteps = reason === CameraError.PERMISSION_DENIED && isStandalone;

    return (
        <div className="camera-fallback" data-testid="camera-fallback">
            <h1 className="camera-fallback-title">{copy.title}</h1>
            <p className="camera-fallback-body">{copy.body}</p>

            {showIpadSteps && (
                <ol className="camera-fallback-steps">
                    {IPAD_STEPS.map((step) => <li key={step}>{step}</li>)}
                </ol>
            )}

            {copy.showBrowsers && (
                <>
                    <p className="camera-fallback-subtitle">Browsers that work:</p>
                    <ul className="camera-fallback-list">
                        {SUPPORTED_BROWSERS.map((browser) => <li key={browser}>{browser}</li>)}
                    </ul>
                </>
            )}

            {/* Return affordance only — NOT a manual code entry. */}
            <button
                type="button"
                className="url-input-button"
                onClick={() => navigate('/?skipWelcome=true')}
            >
                Go back
            </button>
        </div>
    );
};

export default CameraUnavailable;
